import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Container,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from '@material-ui/core';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Can from 'components/Can';
import { useConfirm } from 'hooks';
import DeleteIcon from '@material-ui/icons/Delete';
import { SearchField } from 'components/FormFields';
import { userGroupsActions } from 'state/modules/userGroups';
import useStyles from './DealerList.styled';
import Layout from 'components/Layout';
import { Table } from 'components/UI';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import { ability } from 'config/ability';

const DealerList = ({ history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userGroups, userGroupsLoading, pagination } = useSelector((state) => state.userGroups);
  const [groupId, setGroupId] = useState(null);
  const { fetchedDeleteUserGroupSuccess } = useSelector((state) => state.userGroups);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    is_customer: false,
  });

  const [toggleDeleteUserGroup, DeleteUserGroupConfirm] = useConfirm({
    title: i18n.t('user_groups.delete_dealer'),
    message: i18n.t('user_groups.delete_dealer_confirm'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(userGroupsActions.deleteUserGroup(groupId));
    },
  });

  useEffect(() => {
    if (fetchedDeleteUserGroupSuccess) {
      fetchUserGroups(params);
      dispatch(userGroupsActions.resetDeleteUserGroup());
    }
    // eslint-disable-next-line
  }, [fetchedDeleteUserGroupSuccess])

  const handleDeleteGroupModal = (id) => {
    setGroupId(id);
    toggleDeleteUserGroup();
  };

  const ActionsMenu = ({ row }) => {
    const handleClick = () => handleDeleteGroupModal(row?.id);
    return (
      <Tooltip title={i18n.t('user_groups.delete_dealer')}>
        <IconButton
          variant="contained"
          aria-haspopup="true"
          onClick={handleClick}
          aria-controls="card-menu"
          className={classes.cardHeaderActions}
          data-cy={`user-groups-list-delete-${row?.id}`}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      sortable: true,
      grow: 1,
      maxWidth: '80px',
      minWidth: '80px',
    },
    {
      name: i18n.t('common.name'),
      cell: (row) => row?.name || '--',
      selector: 'name',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.address'),
      cell: (row) => row?.address || '--',
      selector: 'address',
      sortable: true,
      grow: 3,
    },
    {
      name: i18n.t('common.phone'),
      cell: (row) => row?.phone_number || '--',
      selector: 'phone_number',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      cell: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">{row.status.toLowerCase()}</span>
          </div>
        ) : (
          '--'
        ),
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => <ActionsMenu row={row} />,
      button: true,
      center: true,
      grow: 0,
      allowOverflow: true,
      sortable: false,
    },
  ];

  const fetchUserGroups = useCallback(
    (params = {}) => {
      dispatch(userGroupsActions.fetchUserGroups(params, 'business'));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchUserGroups(params);
  }, [fetchUserGroups, params]);

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    setParams({ ...params, ...newParams });
  };
  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    setParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    setParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    if (ability.can('read', 'user_group')) {
      dispatch(userGroupsActions.resetOnboarding());
      history.push(buildUrl(`/dealers/${row.id}`));
    }
  };

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    setParams({ ...filters });
  };

  return (
    <Layout title="Dealers">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} mb={1}>
                    {i18n.t('user_groups.dealers')}
                  </Box>
                </Typography>
              </Grid>
              <Can I="create" a="user_group">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-user-button"
                    onClick={() => {
                      dispatch(userGroupsActions.resetOnboarding());
                      history.push(buildUrl('/dealers/create'));
                    }}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('user_groups.create_dealer')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={i18n.t('user_groups.search_dealer')}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  field={{
                    onChange: search,
                    name: 'keyword',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={userGroups}
                columns={columns}
                loading={userGroupsLoading}
                onSort={handleSort}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
          <DeleteUserGroupConfirm />
        </Grid>
      </Container>
    </Layout>
  );
};

export default withRouter(DealerList);
